import { Roll } from "@/shared/models/Roll";
import { RollPassStatus } from "@/shared/selectors/RollPassStatus";
import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import { Edit } from "@mui/icons-material";
import { Box, IconButton } from "@mui/material";
import { ColDef, ICellRendererParams } from "ag-grid-enterprise";
import "moment-duration-format";

const formatTons = (value: number | null | undefined): string => {
	if (!value) return "";
	if (typeof value !== "number" || !isFinite(value)) return "";
	return value.toLocaleString("en-US", {
		minimumFractionDigits: 0,
		maximumFractionDigits: 0,
	});
};

type Props = {
	onEdit: (roll: Roll) => void;
};

export const useTableData = ({ onEdit }: Props) => {
	const columnDefs: ColDef<Roll>[] = [
		{
			field: "BrokenAt",
			headerName: "Broken At",
			minWidth: 140,
			flex: 1,
			valueGetter: (params) =>
				dxtToLocalServerTime(
					(params.data as Roll)?.BrokenAt,
					"MM/dd HH:mm:ss",
				) || "",
		},
		{
			field: "PartName",
			headerName: "Name",
			minWidth: 140,
			flex: 1,
		},
		{
			field: "LocationName",
			headerName: "Location",
			minWidth: 120,
			flex: 1,
			valueGetter: (p) => p.data?.LocationName ?? p.data?.LocationKey ?? "",
		},
		{
			field: "Diameter",
			headerName: "Diameter",
			minWidth: 100,
			flex: 1,
			type: "numericColumn",
			valueFormatter: (p) =>
				p.value == null ? "" : Number(p.value).toFixed(2),
		},
		{
			field: "AccumulatedTons",
			headerName: "Accum. Tons",
			minWidth: 110,
			flex: 1,
			type: "numericColumn",
			valueFormatter: (p) => formatTons(p.value as number | null),
		},
		{
			headerName: "Details",
			flex: 1.5,
			sortable: false,
			filter: false,
			cellRenderer: (params: ICellRendererParams<Roll>) => {
				const roll = params.data;
				if (!roll) return null;

				if (roll.RollTypeCode === "E") {
					return (
						<RollPassStatus
							passesStatus={roll.Passes?.map((p) => p.PassStatusCode).join(",")}
							passesDiagonal={roll.Passes?.map((p) =>
								p.IsDiagonal ? "1" : "0",
							).join(",")}
							withLabel={false}
							justifyContent="center"
						/>
					);
				}

				return (
					<Box
						sx={{
							display: "flex",
							gap: 2,
							alignItems: "center",
							lineHeight: 1.1,
							height: "100%",
						}}
					>
						<Box sx={{ display: "flex", flexDirection: "column" }}>
							<span style={{ fontSize: "0.65rem", color: "#757575" }}>
								Arbor
							</span>
							<span style={{ fontSize: "0.85rem" }}>
								{roll.ArborPartName ?? "-"}
							</span>
						</Box>
						<Box sx={{ display: "flex", flexDirection: "column" }}>
							<span style={{ fontSize: "0.65rem", color: "#757575" }}>
								Donut
							</span>
							<span style={{ fontSize: "0.85rem" }}>
								{roll.DonutPartName ?? "-"}
							</span>
						</Box>
					</Box>
				);
			},
		},
		{
			headerName: "",
			colId: "edit",
			minWidth: 80,
			maxWidth: 100,
			sortable: false,
			filter: false,
			cellRenderer: (params: ICellRendererParams<Roll>) => (
				<IconButton
					size="small"
					title="Change Status"
					onClick={() => params.data && onEdit(params.data)}
				>
					<Edit fontSize="small" color="primary" />
				</IconButton>
			),
		},
	];

	return { columnDefs };
};
